// 261005 E1-P1 → E1-P3b — chặng ④ Chia tập: 2 thẻ chế độ inline (không còn modal) · cổng AI `episode_split` · bảng kế hoạch tập
// (Tập · Tóm tắt · [Đoạn nguồn] · Nhịp hấp dẫn · Móc cuối tập; KHÔNG có cột kịch bản / nút viết — việc đó ở ⑤). Repair vẫn ở ② (Leader chốt P3b C1).
// 261015 P4c-2b: cột "Nhịp hấp dẫn" (nhãn biên tập DỰ KIẾN, BE phân loại), thẻ Chốt dàn tập có độ phủ + mọi đoạn tập liền chưa chỉ ra nhịp + "Hỏi trợ lý", lỗi OVERVIEW_NOT_EFFECTIVE có lối "Mở Tổng quan truyện".
import { friendlyApiError } from '../jobErrorText';
import { pickJobId } from '../jobTracking';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Dropdown, Empty, Modal, Radio, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import Button from '../WbButton';
import { adaptationApi } from '../../../../services/api/adaptation';
import { adaptationAssistantApi } from '../../../../services/api/adaptationAssistant';
import type { OutlineEpisode } from '../../../../types/adaptation';
import AdaptationGateCard from '../../AdaptationGateCard';
import {
  buildEpisodesBody, canApprove, describeAdaptationError, formatSourceRanges, gateLabel, humanizeCodes,
} from '../../adaptationUtils';
import StageFrame from '../StageFrame';
import { useCtaHolds, useStageIntent, useWorkbenchCtx } from '../WorkbenchContext';
import { planIsPrimary, splitRedOwners } from '../stageCtaView';
import {
  SPLIT_CARDS, SplitMode, splitCardState, splitModeLabel, splitRunState, currentSplitMode,
  episodeActionStates, lockedThroughOf, describeSplitRejected, formatSourceLength, rewriteActionState,
} from '../splitStageView';
import OptionsButton from '../assistant/OptionsButton';
import { gateIssueOptionsRequest, optionsDisabledReason, optionsRequest, warningSubject } from '../assistant/optionsAction';
import { newRequestId, pickThreadId, rewritableEpisodes, splitAssistAction, splitChipsOf } from '../assistant/assistantView';
import type { SkeletonIssueChip } from '../../../../types/adaptationAssistant';
import { CutModal, EpisodeTextModal } from '../SplitEditModals';
import { EPISODE_PAGE_SIZE, pageOfEpisode } from '../workbenchView';
import { originalNameSummaryNotice } from '../../localizedNameNotice';
import { autoApprovedPlanLine, nsStageApproveButton } from '../newStoryRunView';
import { episodePlanCopy, episodePlanSaveErrorView, overviewBlockOf, submitEpisodePlanApproval } from '../episodePlanView';
import { APPEAL_COLUMN_TITLE, APPEAL_PROMISE_NOTE, appealCellOf, appealNarrowTitle, appealPlanBlock } from '../episodeAppealView';
import { AppealCellView, AppealPlanSummary } from '../EpisodeAppealParts';
import AppendUnusedPanel from '../AppendUnusedPanel';

const { Text } = Typography;

const SplitStage: React.FC = () => {
  const stageCta = useCtaHolds().holds('stage');
  const { storyId, data, workbench, refreshAll, busy, bgJob, showError, stageState, requestAssistant, notifyScriptSaved, mainWidth, goTo } = useWorkbenchCtx();
  const narrowTable = mainWidth < 760;
  /** `null` = theo chế độ đang dùng; chỉ lưu khi người chọn thẻ khác. */
  const [draft, setDraft] = useState<SplitMode | null>(null);
  const [starting, setStarting] = useState(false);
  const [page, setPage] = useState(1);
  /** E3-P2a: hộp thoại sửa tay đang mở. */
  const [editing, setEditing] = useState<{ kind: 'text' | 'split' | 'move'; ep: number } | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  /** 261015 P4c-2b (D9b): truyện có tổng quan mà tổng quan chưa chốt / đã cũ ⇒ BE từ chối chốt dàn tập / chia tập (0 AI, 0 ghi). Hiện khối cảnh báo + lối ra "Mở Tổng quan truyện". */
  const [overviewBlock, setOverviewBlock] = useState<{ text: string; extra: string[] } | null>(null);
  /** E3-P2b: lỗi chia tập do BE tính (GET scope ④ — 0 AI) để mỗi lỗi có nút "Nhờ AI sửa". Lỗi/chưa tải ⇒ không hiện danh sách. */
  const [assistChips, setAssistChips] = useState<SkeletonIssueChip[] | null>(null);
  useEffect(() => { setDraft(null); }, [storyId]);

  const outline = data.record?.outline ?? null;
  const originalNameNotice = originalNameSummaryNotice(outline?.characters);
  const episodes: OutlineEpisode[] = useMemo(() => outline?.episodes ?? [], [outline]);
  const current = currentSplitMode(outline?.splitMode);
  const mode = draft ?? current;
  const isRange = current === 'range';
  const locked = stageState('split') === 'locked';
  const disabled = busy || starting || locked;
  const episodesStale = workbench?.outlineFlags?.episodesStale === true;
  const orderedEps = useMemo(() => episodes.map((e) => e.ep), [episodes]);
  /** M — khoá phần đã viết (cùng định nghĩa BE; BE là chuẩn). */
  const lockedThrough = useMemo(() => lockedThroughOf(episodes, data.scripts), [episodes, data.scripts]);
  const modifiedAt = data.record?.modifiedAt;
  const rewritable = useMemo(() => rewritableEpisodes(episodes, lockedThrough), [episodes, lockedThrough]);
  const hasEpisodes = episodes.length > 0;
  // Workbench và GET outline được nạp song song; ưu tiên workbench mới hơn để một GET cũ không giữ thẻ chốt ở trạng thái cũ.
  const episodePlan = workbench?.episodePlan ?? data.record?.episodePlan;
  const appendWaitingForPlan = workbench?.appendRun?.status === 'WAIT_EPISODE_PLAN';
  /** 261023 E3-P3c1: chuỗi truyện mới — máy đã tự chốt dàn tập sạch. */
  const autoApprovedLine = autoApprovedPlanLine(workbench?.appendRun);
  const stageBtn = nsStageApproveButton(workbench?.appendRun);   // chuỗi truyện mới ở chỗ dừng 2: nút chính ở dải, nút này hạ thành phụ
  const planCopy = episodePlanCopy(episodePlan);
  const approvalGate = canApprove(outline, data.record?.violations);
  const planGateReason = !approvalGate.can && planCopy.action !== 'unapprove' ? approvalGate.reason : undefined;   // 261102 P2: nút bị khoá thì nói vì sao (cùng chữ với tooltip ở dải Phần mới)
  // R4/F1: «Chốt dàn tập» (khi là nút chính) thắng «Chia lại tập» — đúng một nút đỏ ở ④.
  // R5: xét cả «đã có tập» (chưa chia thì không có thẻ dàn tập) và «bấm được» — nếu không, ④ chưa chia lần nào bị 0 nút đỏ.
  const planApprovePrimary = planIsPrimary({ hasEpisodes, planAction: planCopy.action, planBtnPrimary: stageBtn.primary });
  /** 261015 P4c-2b (D13): nhịp hấp dẫn từng tập do BE tính (GET outline) — FE chỉ đọc. Vắng (BE cũ) ⇒ không cột, không khối. */
  const appeal = data.record?.appeal;
  const motives = outline?.overview?.motives;
  const appealBlock = useMemo(() => appealPlanBlock(appeal), [appeal]);
  const violationsSig = JSON.stringify((data.record?.violations ?? []).map((v) => [v.gate, v.message])) + episodes.filter((e) => e.textReview).map((e) => e.ep).join(',');
  useEffect(() => {
    if (!hasEpisodes) { setAssistChips(null); return undefined; }
    let alive = true;
    adaptationAssistantApi.scope({ storyId, stageKey: 'split', scopeKey: '' })
      .then((view) => { if (alive) setAssistChips(splitChipsOf(view)); })
      .catch(() => { if (alive) setAssistChips(null); });
    return () => { alive = false; };
  }, [storyId, hasEpisodes, violationsSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const lintChips = useMemo(() => (assistChips ?? []).filter((c) => c.gate !== 'TEXT_REVIEW'), [assistChips]);

  /** "Nhờ AI viết lại": lấy/tạo luồng ④ → `propose-rewrite` (lượt KHÔNG AI + khối PROPOSED) → mở khung Trợ lý. AI chỉ chạy khi người bấm "Xem thử" ở khối. */
  const proposeRewrite = async (eps: number[]) => {
    setRewriting(true);
    try {
      const params = { storyId, stageKey: 'split' as const, scopeKey: '' };
      const threads = await adaptationAssistantApi.listThreads(params);
      const tid = pickThreadId(threads, null) ?? (await adaptationAssistantApi.createThread(params)).id;
      await adaptationAssistantApi.proposeRewrite(tid, { requestId: newRequestId(), eps });
      requestAssistant({ stageKey: 'split', issueIds: [] });
      notifyScriptSaved();   // khung Trợ lý đang mở ⇒ tải lại luồng để thấy khối mới
      message.success(`Đã tạo đề xuất viết lại tập ${eps.join(', ')} — mở khung Trợ lý AI, bấm "Xem thử" (tốn AI) để AI viết.`, 6);
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không tạo được đề xuất viết lại'), 6);
    } finally { setRewriting(false); }
  };
  const rewriteState = (eps: number[]) => rewriteActionState({ eps, lockedThrough, stale: episodesStale, busy: disabled, starting: rewriting });
  const editingEp = editing ? episodes.find((e) => e.ep === editing.ep) ?? null : null;
  const editingNext = editing ? episodes.find((e) => e.ep === editing.ep + 1) ?? null : null;

  /** Gộp: xem thử → so mốc màn hình → hỏi → PATCH kèm `expectedModifiedAt`. */
  const mergeWithNext = async (ep: number) => {
    try {
      const pv = await adaptationApi.splitPreview(storyId, [{ op: 'mergeEpisodes', ep }]);
      if (pv.baseModifiedAt && modifiedAt && pv.baseModifiedAt !== modifiedAt) { message.warning('Danh sách tập vừa đổi ở nơi khác — đã tải lại, thử lại', 6); await refreshAll(); return; }
      if (!pv.applied.length) { message.warning(describeSplitRejected(pv.rejected) || 'Không gộp được', 6); return; }
      const after = pv.outlinePreview?.after[0];
      Modal.confirm({
        title: `Gộp tập ${ep} với tập ${ep + 1}?`,
        content: `Tập mới dài ${formatSourceLength(after?.sourceChars, after?.sourceChapters)}; các tập sau dịch số lùi một. Tóm tắt hai tập được nối — cần chấm lại sau khi sửa.`,
        okText: 'Gộp', cancelText: 'Thôi',
        onOk: async () => {
          try {
            const r = await adaptationApi.splitPatch(storyId, [{ op: 'mergeEpisodes', ep }], pv.baseModifiedAt);
            if (!r.written) message.warning(describeSplitRejected(r.rejected) || 'Không gộp được', 6); else message.success(r.applied.map((a) => a.detail).join('; '));
            await refreshAll();
          } catch (e: any) {
            if (e?.response?.data?.code === 'OUTLINE_CHANGED') { message.warning('Danh sách tập vừa đổi ở nơi khác — đã tải lại, thử lại', 6); await refreshAll(); } else message.error(friendlyApiError(e, 'Không gộp được'), 6);
          }
        },
      });
    } catch (e: any) { message.error(friendlyApiError(e, 'Không xem thử được'), 6); }
  };

  const run = splitRunState({
    draftMode: mode, current, hasEpisodes: episodes.length > 0, lockedThrough, busy: disabled, locked,
    total: Number(outline?.params?.episodes) || undefined,
  });
  const red = splitRedOwners({ stageCta, hasEpisodes, planAction: planCopy.action, planBtnPrimary: stageBtn.primary, planEnabled: !(disabled || planSaving), runEnabled: run.enabled });
  const cardOf = (m: SplitMode) => splitCardState({ card: m, busy: disabled, locked });

  useStageIntent('split', (t) => {
    if (t.intent === 'focusEpisode' && t.episode !== undefined) setPage(pageOfEpisode(t.episode, orderedEps));
  });

  const runSplit = async () => {
    setOverviewBlock(null);
    setStarting(true);
    try {
      const started = await bgJob.start(
        'episodes', () => adaptationApi.buildEpisodes(storyId, buildEpisodesBody(mode)),
        `Chia tập ${mode === 'range' ? 'theo đoạn nguồn' : 'theo chương'}`,
      );
      setDraft(null);
      if (!started) await refreshAll();
    } catch (e) {
      const ob = overviewBlockOf(e);
      if (ob) { setOverviewBlock(ob); message.warning(ob.text, 6); } else showError(describeAdaptationError(e));
    } finally {
      setStarting(false);
    }
  };

  /** MỘT hàm chạy chia: nút chính, menu ⋯ và "Sửa lại" của cổng AI đều qua đây. Z4: chặn ở client — 403 sẽ khiến interceptor đăng xuất. */
  const startSplit = () => {
    if (!run.enabled) return;
    if (run.confirm) {
      const c = run.confirm;
      Modal.confirm({ title: c.title, content: `${c.content} Chạy nền, tốn AI${run.partial ? '.' : ' — áp dụng cho toàn bộ truyện.'}`, okText: c.okText, cancelText: 'Thôi', okButtonProps: { danger: c.danger }, onOk: () => runSplit() });
    } else Modal.confirm({ title: `${run.label}?`, content: 'Chạy nền, tốn AI — chia lại toàn bộ truyện theo chế độ đã chọn.', okText: run.label, cancelText: 'Thôi', onOk: () => runSplit() });
  };

  /** Chốt có CAS: lấy đúng mốc GET outline; append chỉ tiếp tục sau khi chốt thành công. */
  const saveEpisodePlan = async (action: 'approve' | 'unapprove') => {
    if (!modifiedAt) { message.warning('Dàn tập vừa đổi — đã tải lại, thử lại', 5); await refreshAll(); return; }
    setPlanSaving(true);
    setOverviewBlock(null);
    try {
      if (action === 'approve') {
        // Append là một lệnh nguyên tử ở BE: continue tự chốt theo CAS (đúng `modifiedAt` đang xem) rồi mới mở writer.
        await submitEpisodePlanApproval(adaptationApi, { storyId, modifiedAt, appendWaiting: appendWaitingForPlan });
        message.success(appendWaitingForPlan ? 'Đã chốt dàn tập và bắt đầu viết tập mới' : 'Đã chốt dàn tập');
      } else {
        await adaptationApi.unapproveEpisodePlan(storyId, modifiedAt);
        message.success('Đã bỏ chốt dàn tập');
      }
      await refreshAll();
    } catch (e: any) {
      const v = episodePlanSaveErrorView(e, 'Không đổi được trạng thái chốt dàn tập');
      if (v.exit === 'openOverview') setOverviewBlock({ text: v.text, extra: v.extra ?? [] });
      if (v.level === 'warning') message.warning(v.text, 6); else message.error(v.text, 6);
      await refreshAll();
    } finally { setPlanSaving(false); }
  };

  const primary = (
    <Tooltip title={run.enabled ? undefined : run.reason}>
      <span>
        <Button
          type="primary" cta={red.run} loading={bgJob.bgJob?.key === 'episodes' || bgJob.starting === 'episodes'}
          disabled={!run.enabled} onClick={startSplit}
        >{run.label}</Button>
      </span>
    </Tooltip>
  );

  return (
    <StageFrame
      stage="split" primary={primary}
      redo={{ label: 'Chia lại tập', run: startSplit, disabled: !run.enabled }}
      gate={{ label: 'Chia tập', gate: outline?.reviewGates?.episode_split }}
    >
      {originalNameNotice && (
        <Alert style={{ marginBottom: 12 }} type="info" showIcon message={originalNameNotice} />
      )}
      <div id="section-split-mode" style={{ marginBottom: 12 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text strong>Cách chia tập</Text>
          <Radio.Group value={mode} onChange={(e) => setDraft(e.target.value)} style={{ width: '100%' }} aria-label="Cách chia tập">
            <div className="wb-choices">
              {SPLIT_CARDS.map((c) => {
                const st = cardOf(c.mode);
                return (
                  <Tooltip key={c.mode} title={st.enabled ? undefined : st.reason}>
                    <Radio value={c.mode} className="wb-choice" disabled={!st.enabled && c.mode === 'range'}>
                      <Space size={6} wrap>
                        <Text strong>{c.title}</Text>
                        {episodes.length > 0 && current === c.mode && <Tag color="blue" style={{ margin: 0 }}>Đang dùng</Tag>}
                      </Space>
                      <span className="wb-choice-hint">{c.hint}</span>
                    </Radio>
                  </Tooltip>
                );
              })}
            </div>
          </Radio.Group>
          {!run.enabled && run.reason && !disabled && <Text type="warning" style={{ fontSize: 13 }}>{run.reason}</Text>}
          {run.enabled && draft && draft !== current && episodes.length > 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>Sẽ đổi cách chia từ {splitModeLabel(current)} sang {splitModeLabel(draft)}.</Text>
          )}
        </Space>
      </div>

      {episodesStale && (
        <Alert
          style={{ marginBottom: 12 }} type="warning" showIcon
          message="Danh sách tập đã cũ sau khi dựng lại khung xương — bấm Chia lại tập (tập đã viết giữ nguyên)"
        />
      )}

      {overviewBlock && (
        <Alert
          style={{ marginBottom: 12 }} type="warning" showIcon closable onClose={() => setOverviewBlock(null)} message={overviewBlock.text}
          description={overviewBlock.extra.length ? overviewBlock.extra.map((x, i) => <div key={i} style={{ fontSize: 13 }}>• {x}</div>) : undefined}
          action={<Button size="small" type="primary" onClick={() => { setOverviewBlock(null); goTo({ stage: 'skeleton', intent: 'openOverview' }); }}>Mở Tổng quan truyện</Button>}
        />
      )}

      {autoApprovedLine && <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 8 }}>{autoApprovedLine}</Text>}
      {hasEpisodes && (
        <Alert
          style={{ marginBottom: 12 }} type={planCopy.tone} showIcon message={planCopy.text}
          description={(appendWaitingForPlan || appealBlock || planGateReason) ? (
            <div>
              {appendWaitingForPlan && <div>Phần mới đã chia xong. Chốt dàn tập để bắt đầu viết các tập mới.</div>}
              {planGateReason && <div style={{ color: 'var(--wb-warn-fg)' }}><b>{planGateReason}</b></div>}
              {appealBlock && (
                <AppealPlanSummary
                  block={appealBlock} askDisabled={locked}
                  onAsk={() => requestAssistant({ stageKey: 'split', issueIds: [], suggestedText: appealBlock.askText })}
                />
              )}
            </div>
          ) : undefined}
          action={planCopy.action === 'resplit' ? (
            <Button size="small" disabled={disabled} onClick={startSplit}>Chia lại tập</Button>
          ) : (
            <Space size={8}>
              {appendWaitingForPlan && stageBtn.hint && <Text type="secondary" style={{ fontSize: 13 }}>{stageBtn.hint}</Text>}
              <Button size="small" type={planApprovePrimary ? 'primary' : 'default'} cta={red.plan} loading={planSaving} disabled={disabled || planSaving || (planCopy.action !== 'unapprove' && !approvalGate.can)} onClick={() => saveEpisodePlan(planCopy.action === 'unapprove' ? 'unapprove' : 'approve')}>
                {planCopy.action === 'approve' ? (appendWaitingForPlan ? 'Chốt & viết tập mới' : 'Chốt dàn tập') : planCopy.action === 'reapprove' ? 'Chốt lại' : 'Bỏ chốt'}
              </Button>
            </Space>
          )}
        />
      )}

      <AdaptationGateCard
        storyId={storyId} stepKey="episode_split" gates={outline?.reviewGates} hasResult={episodes.length > 0}
        onRerunStep={startSplit} rerunAlreadyConfirmed disabled={disabled || !run.enabled} adoptJobId={pickJobId(workbench?.jobs.running, 'split', 'gate')} onChanged={() => { refreshAll(); }}
        onAskOptions={(issue) => { const req = gateIssueOptionsRequest('episode_split', issue); if (req) requestAssistant(req); }}
        askOptionsDisabledReason={optionsDisabledReason({ stageLocked: locked })}
      />

      {rewritable.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }} type="info" showIcon
          message={`Tập ${rewritable.join(', ')} có tóm tắt/móc cần xem lại sau khi đổi ranh giới.`}
          description="Có thể sửa tay, hoặc nhờ AI viết lại theo đúng đoạn nguồn mới (tốn AI, chỉ chạy khi bạn bấm Xem thử ở khung Trợ lý)."
          action={(
            <Tooltip title={rewriteState(rewritable).enabled ? undefined : rewriteState(rewritable).reason}>
              <span><Button size="small" loading={rewriting} disabled={!rewriteState(rewritable).enabled} onClick={() => proposeRewrite(rewritable)}>Nhờ AI viết lại tất cả</Button></span>
            </Tooltip>
          )}
        />
      )}
      {lintChips.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }} type="warning" showIcon message="Cảnh báo của chia tập"
          description={lintChips.map((c) => (
            <div key={c.id} style={{ fontSize: 13 }}>
              {c.severity === 'ERROR' ? '⛔ ' : ''}{gateLabel(c.gate)} — {humanizeCodes(c.message)}{c.refs.length ? ` (${c.refs.join(', ')})` : ''}{' '}
              <Button size="small" type="link" onClick={() => requestAssistant(splitAssistAction(c).intent)}>{splitAssistAction(c).label}</Button>{' '}
              <OptionsButton
                disabledReason={optionsDisabledReason({ stageLocked: locked })}
                onClick={() => requestAssistant(optionsRequest({ stageKey: 'split', issueIds: [c.id] }, warningSubject('chia tập', c)))}
              />
            </div>
          ))}
        />
      )}
      <AppendUnusedPanel />
      {episodes.length === 0 ? (
        <Empty description="Chưa chia tập — duyệt khung xương rồi bấm Chia tập" />
      ) : (
        <Table
          rowKey="ep" size="small" dataSource={episodes}
          onRow={(r: OutlineEpisode) => ({ id: `outline-ref-${r.ep}` })}
          pagination={episodes.length > EPISODE_PAGE_SIZE
            ? { pageSize: EPISODE_PAGE_SIZE, size: 'small', current: page, onChange: (p) => setPage(p), showSizeChanger: false }
            : false}
          columns={[
            { title: 'Tập', dataIndex: 'ep', key: 'ep', width: 60 },
            {
              title: 'Tóm tắt', dataIndex: 'synopsis', key: 'synopsis', ellipsis: true,
              render: (v: string, e: OutlineEpisode) => (
                <span title={narrowTable ? appealNarrowTitle(e, appealCellOf(e, appeal, motives)) : undefined}>
                  {e.textReview && <Tag color="orange" style={{ marginRight: 6 }}>Cần chấm lại sau khi sửa</Tag>}
                  {e.textReview && e.ep > lockedThrough && (
                    <Tooltip title={rewriteState([e.ep]).enabled ? undefined : rewriteState([e.ep]).reason}>
                      <span><Button size="small" type="link" style={{ padding: 0, marginRight: 6 }} disabled={!rewriteState([e.ep]).enabled} onClick={() => proposeRewrite([e.ep])}>Nhờ AI viết lại</Button></span>
                    </Tooltip>
                  )}
                  {v}
                </span>
              ),
            },
            ...(isRange ? [{
              title: 'Đoạn nguồn', key: 'sourceRanges', width: 200,
              render: (_: unknown, e: OutlineEpisode) => {
                const label = formatSourceRanges(e.sourceRanges);
                return label ? <Text style={{ fontSize: 13 }}>{label}</Text> : <Text type="secondary" style={{ fontSize: 13 }}>—</Text>;
              },
            }] : []),
            // 261015 P4c-2b (Q8-5): thứ tự Tóm tắt · [Đoạn nguồn] · Nhịp hấp dẫn · Móc. Màn hẹp: gộp vào tooltip ô Tóm tắt (như cột Móc).
            ...(narrowTable || !appeal ? [] : [{
              title: <Tooltip title={APPEAL_PROMISE_NOTE}><span>{APPEAL_COLUMN_TITLE}</span></Tooltip>, key: 'appeal', width: 260,
              render: (_: unknown, e: OutlineEpisode) => <AppealCellView cell={appealCellOf(e, appeal, motives)} />,
            }]),
            ...(narrowTable ? [] : [{
              title: 'Móc cuối tập', dataIndex: 'hook', key: 'hook', ellipsis: true,
              render: (v: string, e: OutlineEpisode) => (
                <Tooltip title={e.suspense ? `Còn treo: ${e.suspense}` : undefined}>
                  <span>{v || '—'}</span>
                </Tooltip>
              ),
            }]),
            {
              title: 'Sửa', key: 'actions', width: narrowTable ? 110 : 280,
              render: (_: unknown, e: OutlineEpisode) => {
                const a = episodeActionStates({ ep: e.ep, total: episodes.length, lockedThrough, stale: episodesStale, busy: disabled, mode: current, chapterCount: (e.sourceChapters ?? []).length });
                const btn = (label: string, st: { enabled: boolean; reason?: string }, onClick: () => void) => (
                  <Tooltip title={st.enabled ? undefined : st.reason}><span><Button size="small" disabled={!st.enabled} onClick={onClick}>{label}</Button></span></Tooltip>
                );
                if (narrowTable) {
                  return (
                    <Space size={4} wrap>
                      {btn('Sửa', a.edit, () => setEditing({ kind: 'text', ep: e.ep }))}
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            { key: 'split', label: 'Tách', disabled: !a.split.enabled, title: a.split.reason },
                            { key: 'merge', label: 'Gộp với tập sau', disabled: !a.merge.enabled, title: a.merge.reason },
                            { key: 'move', label: 'Dời điểm cắt', disabled: !a.move.enabled, title: a.move.reason },
                          ],
                          onClick: ({ key }) => {
                            if (key === 'split') setEditing({ kind: 'split', ep: e.ep });
                            else if (key === 'merge') mergeWithNext(e.ep);
                            else setEditing({ kind: 'move', ep: e.ep });
                          },
                        }}
                      >
                        <Button size="small" aria-label="Thêm thao tác của tập">⋯</Button>
                      </Dropdown>
                    </Space>
                  );
                }
                return (
                  <Space size={4} wrap>
                    {btn('Sửa', a.edit, () => setEditing({ kind: 'text', ep: e.ep }))}
                    {btn('Tách', a.split, () => setEditing({ kind: 'split', ep: e.ep }))}
                    {btn('Gộp với tập sau', a.merge, () => { mergeWithNext(e.ep); })}
                    {btn('Dời điểm cắt', a.move, () => setEditing({ kind: 'move', ep: e.ep }))}
                  </Space>
                );
              },
            },
          ] as any}
        />
      )}
      <EpisodeTextModal
        storyId={storyId} modifiedAt={modifiedAt} episode={editing?.kind === 'text' ? editingEp : null} motives={motives} appeal={appeal}
        onClose={() => setEditing(null)} onChanged={() => refreshAll()}
      />
      <CutModal
        storyId={storyId} modifiedAt={modifiedAt} kind={editing?.kind === 'move' ? 'move' : 'split'} mode={current}
        episode={editing && editing.kind !== 'text' ? editingEp : null} next={editingNext}
        onClose={() => setEditing(null)} onChanged={() => refreshAll()}
      />
    </StageFrame>
  );
};

export default SplitStage;
